import React from 'react';
import { components, MultiValueProps, SelectComponentsConfig, ValueContainerProps } from 'react-select';

export interface SelectionSummary {
  singular: string;
  plural: string;
  /** What the control is for, e.g. "Filter by client". The count of chosen values is appended. */
  ariaLabel?: string;
}

interface SummaryOption {
  label?: string;
}

/** One chosen: its label. Several: a count with the noun, e.g. "3 clients". */
export const summariseSelection = (labels: string[], { plural }: SelectionSummary): string =>
  labels.length === 1 ? labels[0] : `${labels.length} ${plural}`;

/** The control's accessible name, with what is chosen appended, e.g. "Filter by client, 3 selected". */
export const summaryAriaLabel = (base: string | undefined, count: number): string | undefined =>
  base && count > 0 ? `${base}, ${count} selected` : base;

// Module-level components, so their identity is stable across renders. (A component created inside a
// render is a new type every time, which remounts the value container and its search input and drops
// focus.) The summary's wording reaches MultiValue through the select's `summary` prop.
const SummaryValueContainer = (props: ValueContainerProps<any, boolean>) => (
  // Marks the value container so its layout (one shared cell for the summary and the input) can be
  // set in CSS for this opt-in path only.
  <components.ValueContainer {...props} className="has-selection-summary" />
);

const SummaryMultiValue = (props: MultiValueProps<any, boolean>) => {
  if (props.index !== 0 || props.selectProps.inputValue) return null;

  const { summary } = props.selectProps as unknown as { summary: SelectionSummary };
  const labels = (props.selectProps.value as SummaryOption[]).map((option) => option.label ?? '');
  return (
    <div className="selection-summary" title={labels.join(', ')}>
      {labels.length === 1 ? (
        labels[0]
      ) : (
        <>
          <span className="selection-summary-full">{summariseSelection(labels, summary)}</span>
          <span className="selection-summary-short">{`${labels.length} selected`}</span>
        </>
      )}
    </div>
  );
};

/**
 * Opt-in react-select `components` for a compact multi-select: instead of a chip per value (which
 * squash to unreadable slivers in a narrow control) the first "chip" renders one line of plain text
 * and the rest render nothing. The summary is hidden while the user is typing to search, so the
 * input has the room. The full list of chosen labels is in its title. The select must also be given
 * `summary={...}` (the wording), which `MultiValue` reads from its props.
 */
export const summaryComponents: SelectComponentsConfig<any, boolean, any> = {
  ValueContainer: SummaryValueContainer,
  MultiValue: SummaryMultiValue,
};
